
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Footer from "../components/Homepage/Footer/Footer";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  IconButton,
  MenuItem,
  TextField,
  Typography,
  Stack,
  Chip,
  CircularProgress,
  Paper,
} from "@mui/material";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import SendIcon from "@mui/icons-material/Send";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import Navbar from "../components/Homepage/Navbar/Navbar";

// const API_URL = "http://127.0.0.1:5000";

const API_URL = "https://rukap.edu.in/leave-api";

const NAVY = "#0B3C5D";

const getToday = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const formatDate = (date) => {
  if (!date) return "-";
  const [year, month, day] = date.split("-");
  return `${day}-${month}-${year}`;
};

const ApplyLeave = () => {
  const navigate = useNavigate();

  const [employee, setEmployee] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);

  const [dateInput, setDateInput] = useState("");
  const [selectedDates, setSelectedDates] = useState([]);
  const [purpose, setPurpose] = useState("");
  const [leaveType, setLeaveType] = useState("CASUAL");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const token = sessionStorage.getItem("leaveToken");

  useEffect(() => {
    let cancelled = false;

    const loadEmployee = async () => {
      if (!token) {
        navigate("/leave-portal", { replace: true });
        return;
      }

      try {
        const storedUser = JSON.parse(
          sessionStorage.getItem("leaveUser") || "null"
        );

        if (!storedUser || storedUser.role !== "EMPLOYEE") {
          navigate("/leave-portal", { replace: true });
          return;
        }

        const response = await axios.get(
          `${API_URL}/api/employee/me`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!cancelled) {
          setEmployee(response.data.employee || response.data);
        }
      } catch (err) {
        if (cancelled) return;

        if (
          err.response?.status === 401 ||
          err.response?.status === 403
        ) {
          sessionStorage.removeItem("leaveToken");
          sessionStorage.removeItem("leaveUser");
          navigate("/leave-portal", { replace: true });
          return;
        }

        setError(
          err.response?.data?.message ||
          "Unable to load employee information. Please try again."
        );
      } finally {
        if (!cancelled) setProfileLoading(false);
      }
    };

    loadEmployee();

    return () => {
      cancelled = true;
    };
  }, [navigate, token]);

  const handleAddDate = () => {
    setError("");
    setSuccess("");

    if (!dateInput) {
      setError("Please select a leave date.");
      return;
    }

    if (dateInput < getToday()) {
      setError("Past dates cannot be selected.");
      return;
    }

    if (selectedDates.includes(dateInput)) {
      setError("This date has already been selected.");
      return;
    }

    setSelectedDates((previous) =>
      [...previous, dateInput].sort()
    );

    setDateInput("");
  };

  const handleRemoveDate = (date) => {
    setSelectedDates((previous) =>
      previous.filter((item) => item !== date)
    );
  };
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  
const handleSubmit = async (event) => {
  event.preventDefault();
  setError("");
  setSuccess("");

  if (!token) {
    setError("Please log in before applying for leave.");
    window.scrollTo({ top: 0, behavior: "smooth" });
    return;
  }

  if (selectedDates.length === 0) {
    setError("Please select at least one leave date.");
    window.scrollTo({ top: 0, behavior: "smooth" });
    return;
  }

  if (!purpose.trim()) {
    setError("Please enter the purpose of your leave.");
    window.scrollTo({ top: 0, behavior: "smooth" });
    return;
  }

  setSubmitting(true);
  console.log("Submitting leave application...");
console.log("API URL:", `${API_URL}/api/leaves`);
console.log("Leave type:", leaveType);
console.log("Selected dates:", selectedDates);

  try {
    const response = await axios.post(
      `${API_URL}/api/leaves`,
      {
        leave_type: leaveType,
        dates: selectedDates,
        purpose: purpose.trim(),
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );
console.log("Leave API response:", response.status, response.data);
    if (response.data.success) {
      const applicationNo =
        response.data.application?.application_no;

      setSuccess(
        applicationNo
          ? `Leave application submitted successfully. Application No: ${applicationNo}`
          : "Leave application submitted successfully."
      );

      setSelectedDates([]);
      setDateInput("");
      setPurpose("");
      setLeaveType("CASUAL");
    } else {
      setError(
        response.data.message ||
          "The leave application could not be submitted."
      );
    }
  } catch (err) {
    if (
      err.response?.status === 401 ||
      err.response?.status === 403
    ) {
      setError(
        err.response?.data?.message ||
          "Your session has expired. Please log in again."
      );
    } else {
      setError(
        err.response?.data?.message ||
          "Unable to submit your leave application. Please try again."
      );
    }
  } finally {
    setSubmitting(false);

    // Scroll after React has updated the notification.
    window.requestAnimationFrame(() => {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    });
  }
};

  return (
    <>
      <Navbar />

      <Box
        sx={{
          minHeight: "80vh",
          py: { xs: 2, sm: 3 },
          backgroundColor: "#F7F8FA",
        }}
      >
        <Container maxWidth="md">
          {/* Formal university heading */}
          <PaperHeader
            employee={employee}
            profileLoading={profileLoading}
            navigate={navigate}
          />

          <Card
            variant="outlined"
            sx={{
              borderRadius: 1,
              borderColor: "#D8DEE5",
              boxShadow: "none",
            }}
          >
            <Box
              sx={{
                px: { xs: 2, sm: 3 },
                py: 2,
                backgroundColor: "#F1F4F7",
                borderBottom: "1px solid #D8DEE5",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: NAVY,
                  fontSize: { xs: "1.1rem", sm: "1.25rem" },
                  fontWeight: 600,
                }}
              >
                Leave Application
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Complete the details below to submit your application.
              </Typography>
            </Box>

            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              {error && (
                <Alert
                  severity="error"
                  sx={{ mb: 2 }}
                  onClose={() => setError("")}
                >
                  {error}
                </Alert>
              )}

              {success && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {success}
                  <Box sx={{ mt: 1 }}>
                    <Button
                      size="small"
                      onClick={() =>
                        navigate("/leave-portal/my-applications")
                      }
                    >
                      View My Applications
                    </Button>
                  </Box>
                </Alert>
              )}

              {profileLoading ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <CircularProgress size={28} />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    Loading employee information...
                  </Typography>
                </Box>
              ) : employee ? (
                <Box component="form" onSubmit={handleSubmit}>
                  {/* Employee identity */}
                  <Box
                    sx={{
                      p: 2,
                      mb: 3,
                      border: "1px solid #D8DEE5",
                      borderLeft: `4px solid ${NAVY}`,
                      borderRadius: 1,
                      backgroundColor: "#FFFFFF",
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                    >
                      <Box
                        sx={{
                          width: 42,
                          height: 42,
                          flexShrink: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "#EAF0F5",
                          color: NAVY,
                          borderRadius: 1,
                        }}
                      >
                        <EventAvailableIcon />
                      </Box>

                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                        >
                          Employee Name
                        </Typography>

                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            color: NAVY,
                            overflowWrap: "anywhere",
                          }}
                        >
                          {employee.employee_name || "-"}
                        </Typography>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ overflowWrap: "anywhere" }}
                        >
                          Employee Code: {employee.employee_code || "-"}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>

                  {/* Leave type */}
                  <TextField
                    select
                    fullWidth
                    label="Leave Type"
                    value={leaveType}
                    onChange={(e) => setLeaveType(e.target.value)}
                    sx={{ mb: 3 }}
                  >
                    <MenuItem value="CASUAL">Casual Leave</MenuItem>
                    <MenuItem value="EARNED">Earned Leave</MenuItem>
                    <MenuItem value="MEDICAL">Medical Leave</MenuItem>
                    <MenuItem value="OTHER">Other Leave</MenuItem>
                  </TextField>

                  {/* Date selection */}
                  <Typography
                    variant="subtitle1"
                    sx={{ color: NAVY, fontWeight: 600, mb: 0.5 }}
                  >
                    Leave Dates
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Select each required date and add it to your application.
                  </Typography>

                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1.5}
                    alignItems="stretch"
                  >
                    <TextField
                      label="Select Date"
                      type="date"
                      value={dateInput}
                      onChange={(e) => setDateInput(e.target.value)}
                      inputProps={{ min: getToday() }}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />

                    <Button
                      variant="outlined"
                      onClick={handleAddDate}
                      sx={{
                        minWidth: { xs: "100%", sm: 125 },
                        borderColor: NAVY,
                        color: NAVY,
                        textTransform: "none",
                      }}
                    >
                      Add Date
                    </Button>
                  </Stack>

                  {/* Selected dates */}
                  <Box sx={{ mt: 3, mb: 3 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, mb: 1 }}
                    >
                      Selected Dates ({selectedDates.length})
                    </Typography>

                    {selectedDates.length === 0 ? (
                      <Box
                        sx={{
                          p: 2,
                          border: "1px dashed #B8C4CF",
                          borderRadius: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          color="text.secondary"
                        >
                          No leave dates selected.
                        </Typography>
                      </Box>
                    ) : (
                      <Stack spacing={1}>
                        {selectedDates.map((date) => (
                          <Box
                            key={date}
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              gap: 1,
                              p: 1,
                              border: "1px solid #D8DEE5",
                              borderRadius: 1,
                              backgroundColor: "#FFFFFF",
                            }}
                          >
                            <Chip
                              size="small"
                              icon={<EventAvailableIcon />}
                              label={formatDate(date)}
                              variant="outlined"
                              sx={{
                                maxWidth: "calc(100% - 40px)",
                                borderColor: "#D8DEE5",
                              }}
                            />

                            <IconButton
                              aria-label={`Remove ${formatDate(date)}`}
                              color="error"
                              size="small"
                              onClick={() => handleRemoveDate(date)}
                            >
                              <DeleteOutlineIcon />
                            </IconButton>
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </Box>

                  {/* Total days */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 2,
                      p: 2,
                      mb: 3,
                      border: "1px solid #D8DEE5",
                      borderRadius: 1,
                      backgroundColor: "#F7F8FA",
                    }}
                  >
                    <Typography fontWeight={600}>
                      Total Leave Days
                    </Typography>

                    <Typography
                      variant="h6"
                      sx={{ color: NAVY, fontWeight: 600 }}
                    >
                      {selectedDates.length}
                    </Typography>
                  </Box>

                  {/* Purpose */}
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Purpose / Reason for Leave"
                    placeholder="Enter the reason for your leave"
                    value={purpose}
                    onChange={(e) => setPurpose(e.target.value)}
                    required
                    sx={{ mb: 3 }}
                  />

                  <Divider sx={{ mb: 2.5 }} />

                  <Stack
                    direction={{ xs: "column-reverse", sm: "row" }}
                    spacing={1.5}
                  >
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<ArrowBackIcon />}
                      onClick={() =>
                        navigate("/leave-portal/employee-dashboard")
                      }
                      sx={{
                        py: 1.2,
                        borderColor: NAVY,
                        color: NAVY,
                        textTransform: "none",
                      }}
                    >
                      Back to Dashboard
                    </Button>

                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      disabled={submitting || profileLoading}
                      startIcon={
                        submitting ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : (
                          <SendIcon />
                        )
                      }
                      sx={{
                        py: 1.2,
                        backgroundColor: NAVY,
                        textTransform: "none",
                        "&:hover": {
                          backgroundColor: "#082D46",
                        },
                      }}
                    >
                      {submitting
                        ? "Submitting..."
                        : "Submit Leave Application"}
                    </Button>
                  </Stack>
                </Box>
              ) : (
                <Alert severity="warning">
                  Employee information is unavailable. Please return to
                  the dashboard and try again.
                </Alert>
              )}
            </CardContent>
          </Card>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",
              textAlign: "center",
              mt: 2,
            }}
          >
            Rayalaseema University | Employee Leave Management System
          </Typography>
        </Container>
      </Box>
      <Footer />
    </>
  );
};

// Formal university header with employee identity
function PaperHeader({ employee, profileLoading, navigate }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 2, sm: 2.5 },
        mb: 2,
        borderRadius: 1,
        borderTop: `4px solid ${NAVY}`,
        boxShadow: "none",
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={2}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{
              color: NAVY,
              fontSize: { xs: "1.2rem", sm: "1.5rem" },
              fontWeight: 700,
            }}
          >
            RAYALASEEMA UNIVERSITY
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Kurnool, Andhra Pradesh
          </Typography>

          <Divider sx={{ my: 1.5 }} />

          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, color: "#253443" }}
          >
            Apply for Leave
          </Typography>

          {profileLoading ? (
            <Typography variant="body2" color="text.secondary">
              Loading employee name...
            </Typography>
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ overflowWrap: "anywhere" }}
            >
              Applicant: {employee?.employee_name || "Employee"}
            </Typography>
          )}
        </Box>

        <Button
          variant="outlined"
          onClick={() =>
            navigate("/leave-portal/employee-dashboard")
          }
          sx={{
            alignSelf: { xs: "stretch", sm: "center" },
            borderColor: NAVY,
            color: NAVY,
            textTransform: "none",
          }}
        >
          Back to Dashboard
        </Button>
      </Stack>
    </Paper>
  );
}

export default ApplyLeave;
